import { useMemo } from 'react';

import { AlgoliaApiListingResult } from 'algolia-api/listing/types/AlgoliaApiListingResult';
import { SearchLocation } from 'algolia-api/types/SearchLocation';
import { Carousel, useCarouselState } from 'components/Carousel';
import { RouterLink as Link } from 'components/Link';
import { useIsClient } from 'hooks/useIsClient';
import { range } from 'utils/functional';

import { Section } from './Section';
import {
  TileCarouselContainer,
  TileCarouselSeeAllButton,
} from './TileCarousel.styled';
import { TileCarouselHeader } from './TileCarouselHeader';
import { TileCarouselNavigation } from './TileCarouselNavigation';

type Props = {
  headline: string | null | undefined;
  results: AlgoliaApiListingResult[] | undefined;
  seeAll: {
    text: string;
    action:
      | {
          type: 'link';
          url: string | { pathname: string; search: string };
          state?: Record<string, string | Partial<SearchLocation>>;
        }
      | { type: 'button'; onClick: () => void };
  };
  listingType: string;
  carouselLocation?: string;
  fromListingId?: string;
  layout?: 'SLIM' | 'REGULAR';
  source?: string;
  additionalAnalyticsProps?: Record<string, string>;
};

export function TileCarousel({
  headline,
  results,
  seeAll,
  listingType,
  carouselLocation,
  fromListingId,
  layout = 'REGULAR',
  source,
  additionalAnalyticsProps,
}: Props) {
  const carouselState = useCarouselState({
    slideCount: results?.length ?? 0,
    responsiveSlidesPerPage:
      layout === 'SLIM'
        ? { xs: 1, sm: 1, md: 2, lg: 3 }
        : { xs: 1, sm: 1, md: 3, lg: 4 },
  });

  const carouselHeight = layout === 'SLIM' ? 205 : 338;

  const linkNode = useMemo(() => {
    if (!seeAll) return null;

    switch (seeAll.action.type) {
      case 'link':
        return (
          <Link
            data-qa-id="tile-carousel-see-all"
            to={seeAll.action.url}
            state={seeAll.action.state}
          >
            {seeAll.text}
          </Link>
        );
      case 'button':
        return (
          <TileCarouselSeeAllButton
            data-qa-id="tile-carousel-see-all"
            onClick={seeAll.action.onClick}
          >
            {seeAll.text}
          </TileCarouselSeeAllButton>
        );
    }
  }, [seeAll]);

  const isClient = useIsClient();
  return (
    <>
      <TileCarouselHeader
        layout={layout}
        headline={headline}
        linkNode={linkNode}
      />

      <TileCarouselContainer $layout={layout}>
        <Carousel
          state={carouselState}
          height={carouselHeight}
          data-qa-id="tile-carousel"
          navigation={
            isClient && <TileCarouselNavigation carouselState={carouselState} />
          }
          slides={
            results && results.length > 0
              ? results.map((result) => (
                  <div
                    data-qa-id="tile-carousel-slide"
                    data-qa-listing-id={result.objectID}
                  >
                    <Section
                      result={result}
                      listingType={listingType}
                      carouselLocation={carouselLocation}
                      headline={headline}
                      fromListingId={fromListingId}
                      results={results}
                      layout={layout}
                      source={source}
                      additionalAnalyticsProps={additionalAnalyticsProps}
                    />
                  </div>
                ))
              : range(4).map((index) => (
                  <div key={`section-${index}`}>
                    <div />
                  </div>
                ))
          }
        />
      </TileCarouselContainer>
    </>
  );
}
